import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage, visit } from "@hotwired/turbo"
import { acknowledgeLifecycleActivation, lifecycleActivation } from "./lifecycle_activation_acceptance"
import { signInResponse, validMultiTargetStreamResponse } from "./multi_target_revalidation_stream"
import { activityRequestState } from "./machine_activity_controller"

const ACCEPT = "text/vnd.turbo-stream.html, text/html;q=0.9"
const ACCOUNT_TARGETS = [ "wallet_hud", "profile_affordance" ]
const ROOT_TARGETS = [
  { target: "machine-root", action: "replace", method: null },
  ...ACCOUNT_TARGETS.map((target) => ({ target, action: "update", method: "morph" }))
]

export default class extends Controller {
  static values = { timeout: { type: Number, default: 5_000 } }

  connect() {
    this.desiredGeneration = 0
    this.inFlight = null
    this.pendingIntent = null
    this.submissionHeld = false
    this.acceptedLifecycleIntentId ??= null
  }

  disconnect() {
    this.invalidate()
  }

  activate(event) {
    const activation = lifecycleActivation(event, this.acceptedLifecycleIntentId)
    if (!activation) return
    if (activation.duplicate) {
      activation.acknowledge()
      return
    }
    if (this.held()) {
      if (this.inFlight) this.invalidate()
      return
    }

    if (this.queueIntent("lifecycle")) acknowledgeLifecycleActivation(this, activation)
  }

  selectionChanged(event) {
    if (this.inFlight || this.pendingIntent) this.invalidate()
    if (event.detail?.revalidationEligible !== true) return

    const frame = this.frameAt(event.detail?.departedIndex)
    if (!frame || frame.parentElement?.hasAttribute("inert") !== true) return

    this.queueIntent("settled", frame)
  }

  frameSettled(event) {
    const frame = document.getElementById(this.pendingIntent?.targetId)
    if (this.pendingIntent?.source !== "settled" || !frame?.contains(event.target)) return

    window.setTimeout(() => this.flush(), 0)
  }

  beforeFrameFetch(event) {
    const frame = document.getElementById(this.inFlight?.targetId)
    if (!this.inFlight || !frame?.contains(event.target)) return

    const requeueSettled = this.inFlight.source === "settled"
    this.invalidate()
    if (requeueSettled) {
      this.pendingIntent = { generation: ++this.desiredGeneration, source: "settled", targetId: frame.id }
    }
  }

  submitStart(event) {
    if (!this.selectedSlide()?.contains(event.target)) return

    this.submissionHeld = true
    this.invalidate()
  }

  submitEnd() {
    this.submissionHeld = false
  }

  flush() {
    if (this.inFlight || !this.pendingIntent) return

    const frame = document.getElementById(this.pendingIntent.targetId)
    if (!frame) {
      this.pendingIntent = null
      return
    }
    if (this.pendingIntent.source === "settled" && frame.querySelector("[data-machine-service-mode='true']")) {
      this.pendingIntent = null
      return
    }
    if (this.held(frame)) {
      if (!(this.pendingIntent.source === "settled" && this.transientlyBusy(frame))) this.pendingIntent = null
      return
    }

    const intent = this.pendingIntent
    this.pendingIntent = null
    intent.expectedTargets = this.expectedTargets(frame)
    if (!intent.expectedTargets) return
    this.start(intent, frame)
  }

  queueIntent(source, frame = this.selectedFrame()) {
    if (!frame) return false

    const intent = { generation: ++this.desiredGeneration, source, targetId: frame.id }
    this.pendingIntent = intent
    this.flush()
    return this.pendingIntent === intent || this.inFlight === intent
  }

  start(intent, frame) {
    intent.targetId = frame.id
    intent.url = frame.dataset.src
    intent.abortController = new AbortController()
    intent.timedOut = false
    intent.activityState = activityRequestState(frame)
    this.inFlight = intent
    this.setBusy(frame, true)
    intent.timeoutId = window.setTimeout(() => this.timeout(intent, frame), this.timeoutValue)

    fetch(intent.url, {
      credentials: "same-origin",
      redirect: "follow",
      headers: { Accept: ACCEPT, ...(intent.activityState ? { "X-Machine-Activity": intent.activityState } : {}) },
      signal: intent.abortController.signal
    })
      .then((response) => this.handleResponse(response, intent))
      .catch(() => {})
      .finally(() => this.finish(intent, frame))
  }

  async handleResponse(response, intent) {
    if (signInResponse(response)) {
      this.pendingIntent = null
      this.setBusy(document.getElementById(intent.targetId), false)
      visit(response.url, { action: "replace" })
      return
    }
    if (!response.ok) return

    const body = await response.text()
    if (!this.applicable(intent)) return

    const selectedResponse = validMultiTargetStreamResponse(response, body, intent.expectedTargets)
    const rootResponse = validMultiTargetStreamResponse(response, body, ROOT_TARGETS)
    if (!selectedResponse && !rootResponse) return
    if (rootResponse && intent.source === "settled") return
    const frame = document.getElementById(intent.targetId)
    if (!this.applicable(intent) || this.held(frame)) return
    if (rootResponse && this.structuralCommitHeld()) return

    renderStreamMessage(body)
  }

  applicable(intent) {
    const frame = document.getElementById(intent.targetId)
    return this.inFlight === intent && !intent.timedOut &&
      intent.generation === this.desiredGeneration && frame?.id === intent.targetId &&
      frame.dataset.src === intent.url &&
      activityRequestState(frame) === intent.activityState &&
      (intent.source !== "settled" || frame.parentElement?.hasAttribute("inert") === true)
  }

  timeout(intent, frame) {
    if (this.inFlight !== intent) return

    intent.timedOut = true
    intent.abortController.abort()
    this.setBusy(frame, false)
  }

  finish(intent, frame) {
    if (this.inFlight !== intent) return

    window.clearTimeout(intent.timeoutId)
    this.setBusy(frame, false)
    this.inFlight = null
    this.flush()
  }

  invalidate() {
    this.desiredGeneration += 1
    this.pendingIntent = null
    if (!this.inFlight) return

    const intent = this.inFlight
    this.inFlight = null
    window.clearTimeout(intent.timeoutId)
    intent.abortController.abort()
    this.setBusy(document.getElementById(intent.targetId), false)
  }

  held(frame = this.selectedFrame()) {
    const slide = frame?.parentElement
    if (!slide || document.hidden || this.submissionHeld) return true
    if (this.element.hasAttribute("data-slider-interacting") || frame.hasAttribute("busy")) return true
    if (slide.querySelector("turbo-frame[busy], dialog[open], [data-refresh-hold]")) {
      return true
    }
    if (this.focusedEditable(slide) || this.dirtyForm(slide)) return true

    return false
  }

  transientlyBusy(frame) {
    const slide = frame?.parentElement
    return Boolean(frame?.hasAttribute("busy") || slide?.querySelector("turbo-frame[busy]"))
  }

  focusedEditable(slide) {
    const active = document.activeElement
    return Boolean(active && slide.contains(active) &&
      (active.matches("input, textarea, select") || active.isContentEditable))
  }

  dirtyForm(slide) {
    return Array.from(slide.querySelectorAll("form")).some((form) =>
      Array.from(form.elements).some((control) => {
        if (control.matches("input[type='checkbox'], input[type='radio']")) {
          return control.checked !== control.defaultChecked
        }
        if (control.matches("select")) {
          return Array.from(control.options).some((option) => option.selected !== option.defaultSelected)
        }
        return "defaultValue" in control && control.value !== control.defaultValue
      })
    )
  }

  selectedSlide() {
    const slides = this.element.querySelectorAll(".ve-ds:not([inert])")
    return slides.length === 1 ? slides[0] : null
  }

  selectedFrame() {
    const frames = this.element.querySelectorAll(".ve-ds:not([inert]) > turbo-frame[data-src]")
    return frames.length === 1 ? frames[0] : null
  }

  frameAt(index) {
    if (!Number.isInteger(index) || index < 0) return null

    return this.element.querySelectorAll(".ve-ds > turbo-frame[data-src]")[index] || null
  }

  structuralCommitHeld() {
    const frame = this.selectedFrame()
    return !frame || frame.querySelector("[data-machine-service-mode='true']") || this.held(frame)
  }

  expectedTargets(frame) {
    const machineId = frame?.id.match(/^machine_card_(\d+)$/)?.[1]
    if (!machineId) return null

    const summaryId = `machine_summary_${machineId}`
    const detailsId = `machine_details_${machineId}`
    const activityId = `machine_activity_${machineId}`
    if (frame.querySelectorAll(`#${CSS.escape(summaryId)}`).length !== 1) return null
    if (frame.querySelectorAll(`#${CSS.escape(detailsId)}`).length !== 1) return null
    if (frame.querySelectorAll(`#${CSS.escape(activityId)}`).length !== 1) return null

    return [
      { target: summaryId, action: "replace", method: "morph" },
      { target: detailsId, action: "replace", method: "morph" },
      { target: activityId, action: "replace", method: "morph" },
      ...ACCOUNT_TARGETS.map((target) => ({ target, action: "update", method: "morph" })),
    ]
  }

  setBusy(frame, busy) {
    if (!frame) return
    if (busy) {
      frame.dataset.activationBusy = "true"
      frame.setAttribute("aria-busy", "true")
    } else {
      delete frame.dataset.activationBusy
      frame.removeAttribute("aria-busy")
    }
  }

}
